import React, {useState, useEffect, useMemo} from 'react'
import {Card, Typography} from 'antd'
import classNames from 'classnames'
import {Link} from 'react-router-dom'
import {Icon} from '@/components/dumb/Icon'
import {storage} from '~/code/services/storage'
import {IS_SIDER_COLLAPSED_KEY} from '~/code/constants'
import {useScreenType, useWindowSize} from '~/code/hooks'
import {CarouselWidgetItemBetaProps} from './props'
import styles from './styles.scss'

const {Text, Title} = Typography

export const CarouselWidgetItemBeta: React.FC<CarouselWidgetItemBetaProps> = ({
    variant,
    tagline,
    title,
    description,
    compactDescription,
    bulletPoints,
    linkText,
    linkUrl,
    linkTarget = '_blank',
    customContent,
    onLinkClick
}) => {
    const [isCollapsed, setCollapsed] = useState(() => Boolean(storage.get(IS_SIDER_COLLAPSED_KEY)))
    const [windowWidth] = useWindowSize()
    const {isMobile} = useScreenType()

    const sideBarListener = () => {
        setCollapsed(Boolean(storage.get(IS_SIDER_COLLAPSED_KEY)))
    }

    useEffect(() => {
        window.addEventListener('storage', sideBarListener)
        return () => window.removeEventListener('storage', sideBarListener)
    }, [])

    const clickLinkHandler = (e) => {
        if (onLinkClick) {
            e.preventDefault()
            onLinkClick()
        }
    }

    const isCompactMode = useMemo(() => {
        return (windowWidth < 1300 && !isCollapsed && !isMobile) || isMobile
    }, [windowWidth, isCollapsed, isMobile])

    const getClassName = (defaultClass: string, compactClass: string) => {
        return classNames({
            [styles[defaultClass]]: !isCompactMode,
            [styles[compactClass]]: isCompactMode
        })
    }

    const showImage = !isMobile && !(windowWidth < 1300 && !isCollapsed)

    const variantClass = styles[`variant_${variant}`]

    return (
        <Card className={classNames(styles.cardContainer, variantClass)}>
            <div className={styles.body}>
                <div className={styles.content}>
                    <Text className={getClassName('tagline', 'taglineMobile')}>{tagline}</Text>

                    <Title level={isCompactMode ? 4 : 3} className={styles.title}>
                        {title}
                    </Title>

                    {bulletPoints && bulletPoints.length > 0 && (
                        <ul className={getClassName('bulletList', 'bulletListMobile')}>
                            {bulletPoints.map((point, index) => (
                                <li key={index}>{point}</li>
                            ))}
                        </ul>
                    )}

                    {(description || compactDescription) && (
                        <Text className={getClassName('description', 'descriptionMobile')}>
                            {isCompactMode && compactDescription ? compactDescription : description}
                        </Text>
                    )}

                    {typeof customContent === 'function' ? customContent(isCompactMode) : customContent}

                    {linkText && linkUrl && (
                        <Link
                            to={linkUrl}
                            target={linkTarget}
                            className={getClassName('learnMore', 'learnMoreMobile')}
                            onClick={clickLinkHandler}
                        >
                            {linkText}
                            <span className={styles.arrow}>
                                <Icon type={'arrow_right'} />
                            </span>
                        </Link>
                    )}
                </div>
            </div>

            <div className={getClassName('bgCircle', 'bgCircleMobile')}></div>

            {showImage && <div className={styles.image}></div>}
        </Card>
    )
}
